// Auth domain types — Plan 08-02.
//
// These types are the shape contract between:
//   - apps/web/src/stores/auth.ts          (persistent atom shape)
//   - apps/web/src/auth/exchange.ts        (exchange call result)
//   - apps/web/src/auth/silent-refresh.ts  (re-exchange)
//   - apps/web/src/auth/AuthProvider.tsx   (React context)
//   - apps/web/src/net/client.ts           (typed fetch wrapper)
//
// B12 firewall (CONTEXT line 44): JWT carries user_uuid only; game state
// lives in Durable Objects, never in the client store.

export type IdpKind = 'apple' | 'google'

/**
 * Bearer payload as persisted in localStorage under
 * `runegambit.auth.bearer`. The JWT itself is the SessionToken.session_jwt
 * the backend hands back; exp_ms mirrors SessionToken.expires_at; idp_kind
 * is the originating IdP so we can pick the silent-refresh adapter.
 */
export interface BearerStored {
  jwt: string
  exp_ms: number
  idp_kind: IdpKind
  user_uuid: string
}

/** Result of `exchangeIdpToken` after backend round-trip + atom write. */
export interface AuthExchangeResult {
  user_uuid: string
  session_jwt: string
  exp_ms: number
  idp_kind: IdpKind
}

/**
 * 401 from `/api/auth/exchange` (or any API endpoint via `apiFetch`).
 *
 * Oracle hardening (T-08-AUTH-2 + Phase 2 invariant): the backend returns a
 * byte-equal ErrorEnvelope for every 401 sub-cause. The client maps them all
 * to a single AuthInvalidError with no leakage of cause.
 */
export class AuthInvalidError extends Error {
  readonly code = 'AUTH_INVALID' as const

  constructor(message = 'Authentication invalid') {
    super(message)
    this.name = 'AuthInvalidError'
  }
}

/**
 * 423 LockedPendingDeletion from `/api/auth/exchange` — the user attempted
 * sign-in within the 7-day deletion grace (DEC-022). pending_deletion_until
 * is lawful disclosure to the already-authenticated principal.
 */
export class AuthLockedError extends Error {
  readonly code = 'LOCKED' as const
  readonly pending_deletion_until: number

  constructor(
    pending_deletion_until: number,
    message = 'Account pending deletion',
  ) {
    super(message)
    this.name = 'AuthLockedError'
    this.pending_deletion_until = pending_deletion_until
  }
}

/**
 * 5xx from any API call. Indicates a transient backend fault — caller
 * may retry (apiFetch retries ONCE internally for 5xx).
 */
export class AuthTransientError extends Error {
  readonly code = 'TRANSIENT' as const
  readonly status: number

  constructor(status: number, message = 'Transient backend error') {
    super(message)
    this.name = 'AuthTransientError'
    this.status = status
  }
}
